import { getPlanPermission } from "@utils/permissions/plan-permissions";
import { useState } from "react";
import { RequirePermission } from "@components/common/require-permission";
import { AdmPlan, SubjectType } from "@custom-types/base-types";
import { Button } from "@flat-ui";
import { BulkPlanActionDialog } from "@components/plans-table/bulk-plan-action-dialog";
import {
  BulkPlanAction,
  canDeactivatePlan,
  canDeletePlan,
} from "@utils/plan-actions-utils";
import { TableType } from "@utils/track-event/event-types";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { PlanEvents } from "@utils/track-event/track-event-list";

/** The bulk action whose dialog is open */
interface OpenBulkAction {
  /** The action to apply */
  action: BulkPlanAction;

  /** The plans to apply the action to */
  plans: AdmPlan[];
}

interface Props {
  /** The type of the subject the table belongs to, which decides who may change its plans */
  subjectType: SubjectType;

  /** The plans selected in the table */
  selectedPlans: AdmPlan[];

  /** The table the plans are selected in */
  tableType: TableType;

  /** Function called once an action was applied to the selected plans */
  onApplied(): void;
}

/** Toolbar buttons to deactivate or delete all the plans selected in the plans table at once */
export function BulkPlanActions({
  subjectType,
  selectedPlans,
  tableType,
  onApplied,
}: Props): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  const editPlanPermission = getPlanPermission(subjectType, "edit");
  // The plans are kept as they were when the dialog opened, as deleting them changes the selection
  const [openAction, setOpenAction] = useState<OpenBulkAction | null>(null);

  // Each action only counts the selected plans it applies to: deactivating the ones that are
  // still in effect, and deleting the ones that are already deactivated
  const deactivatablePlans = selectedPlans.filter(canDeactivatePlan);
  const deletablePlans = selectedPlans.filter(canDeletePlan);

  function openDialog(action: BulkPlanAction, plans: AdmPlan[]): void {
    trackEvent({
      name: PlanEvents.openBulkPlanAction,
      props: { location: tableType, action, planCount: plans.length },
    });
    setOpenAction({ action, plans });
  }

  // Stays mounted without a selection, so an open dialog survives the plans it deletes
  return (
    <>
      {selectedPlans.length > 0 && (
        <>
          <RequirePermission permission={editPlanPermission}>
            <Button
              variant="secondary"
              disabled={deactivatablePlans.length === 0}
              onClick={() => openDialog("deactivate", deactivatablePlans)}
            >
              Deactivate ({deactivatablePlans.length})
            </Button>
          </RequirePermission>
          <RequirePermission permission={editPlanPermission}>
            <Button
              variant="destructiveGhost"
              disabled={deletablePlans.length === 0}
              onClick={() => openDialog("delete", deletablePlans)}
            >
              Delete ({deletablePlans.length})
            </Button>
          </RequirePermission>
        </>
      )}
      {openAction && (
        <BulkPlanActionDialog
          action={openAction.action}
          plans={openAction.plans}
          onApplied={onApplied}
          onClose={() => setOpenAction(null)}
        />
      )}
    </>
  );
}
